import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, User, Wrench, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Account() {
  const { user, refresh } = useAuth();
  const [loading, setLoading] = useState(false);
  const [installPrice, setInstallPrice] = useState("");
  const [savingPrice, setSavingPrice] = useState(false);
  const [resaleCoefficient, setResaleCoefficient] = useState("1");
  const [savingResaleCoefficient, setSavingResaleCoefficient] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user) setInstallPrice(String(user.installation_price_ht ?? 0));
    if (user) setResaleCoefficient(String(user.resale_price_coefficient ?? 1));
  }, [user]);

  if (!user) return null;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check size (max 1MB)
    if (file.size > 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 1 Mo)");
      return;
    }
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      toast.error("Format non supporté (PNG, JPG ou WEBP uniquement)");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64 = event.target.result;
      setLoading(true);
      try {
        await api.post("/auth/me/logo", { logo_base64: base64 });
        toast.success("Logo mis à jour");
        await refresh(); // Refresh user context
      } catch (err) {
        toast.error("Impossible de mettre à jour le logo");
      } finally {
        setLoading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = async () => {
    setLoading(true);
    try {
      await api.post("/auth/me/logo", { logo_base64: null });
      toast.success("Logo supprimé");
      await refresh();
    } catch (err) {
      toast.error("Impossible de supprimer le logo");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveInstallPrice = async () => {
    const value = parseFloat(installPrice);
    if (isNaN(value) || value < 0) {
      toast.error("Montant invalide");
      return;
    }
    setSavingPrice(true);
    try {
      await api.post("/auth/me/installation-price", { installation_price_ht: value });
      toast.success("Tarif de pose enregistré");
      await refresh();
    } catch (err) {
      toast.error("Impossible d'enregistrer le tarif de pose");
    } finally {
      setSavingPrice(false);
    }
  };

  const handleSaveResaleCoefficient = async () => {
    const value = parseFloat(resaleCoefficient);
    if (isNaN(value) || value < 1 || value > 10) {
      toast.error("Le coefficient doit être compris entre 1,00 et 10,00");
      return;
    }
    setSavingResaleCoefficient(true);
    try {
      await api.post("/auth/me/resale-coefficient", { resale_price_coefficient: value });
      toast.success("Coefficient de revente enregistré");
      await refresh();
    } catch (err) {
      toast.error("Impossible d'enregistrer le coefficient de revente");
    } finally {
      setSavingResaleCoefficient(false);
    }
  };

  return (
    <main data-testid="account-page" className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 py-8 lg:py-12">
      <div className="mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl">Mon Compte</h1>
        <p className="text-muted-foreground mt-2">Gérez vos informations et votre logo pour la marque blanche.</p>
      </div>

      <div className="paper p-8">
        <div className="flex flex-col sm:flex-row gap-8">
          <div className="flex-1 space-y-4">
            <div>
              <p className="text-xs text-muted-foreground eyebrow mb-1">Société</p>
              <p className="font-medium">{user.company || "-"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground eyebrow mb-1">Contact</p>
              <p>{user.contact_name}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground eyebrow mb-1">Email</p>
              <p>{user.email}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground eyebrow mb-1">SIRET</p>
              <p>{user.siret || "-"}</p>
            </div>
          </div>

          <div className="w-full sm:w-64">
            <p className="text-xs text-muted-foreground eyebrow mb-3">Logo (Marque Blanche PDF)</p>
            <div className="border-2 border-dashed border-border rounded-xl p-4 flex flex-col items-center justify-center text-center gap-3 bg-muted/20">
              {user.logo ? (
                <>
                  <div className="w-full h-24 flex items-center justify-center bg-white rounded border border-border p-2">
                    <img src={user.logo} alt="Logo Artisan" className="max-h-full max-w-full object-contain" />
                  </div>
                  <div className="flex gap-2 w-full mt-2">
                    <Button variant="outline" size="sm" className="w-full text-xs h-8" onClick={() => fileInputRef.current?.click()} disabled={loading}>
                      Modifier
                    </Button>
                    <Button variant="ghost" size="sm" className="px-2 h-8 text-destructive" onClick={handleRemoveLogo} disabled={loading}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-1">
                    <User className="w-6 h-6" />
                  </div>
                  <p className="text-xs text-muted-foreground">Apparaîtra sur vos devis PDF.</p>
                  <Button size="sm" className="w-full mt-2 h-8" onClick={() => fileInputRef.current?.click()} disabled={loading}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-3 h-3 mr-2" />}
                    Uploader un logo
                  </Button>
                </>
              )}
            </div>
            <input 
              type="file" 
              accept="image/png, image/jpeg, image/webp" 
              className="hidden" 
              ref={fileInputRef} 
              onChange={handleFileChange} 
            />
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-border">
          <p className="text-xs text-muted-foreground eyebrow mb-3 flex items-center gap-1.5">
            <Wrench className="w-3.5 h-3.5" /> Forfait de pose
          </p>
          <p className="text-xs text-muted-foreground mb-3 max-w-md">
            Renseignez votre tarif de pose HT. Vous pourrez l'ajouter en option sur chaque devis généré.
          </p>
          <div className="flex items-center gap-2 max-w-xs">
            <div className="relative flex-1">
              <Input
                data-testid="installation-price-input"
                type="number"
                min="0"
                step="1"
                value={installPrice}
                onChange={(e) => setInstallPrice(e.target.value)}
                className="pr-10"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">€ HT</span>
            </div>
            <Button data-testid="save-installation-price-btn" size="sm" className="h-9" onClick={handleSaveInstallPrice} disabled={savingPrice}>
              {savingPrice ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </div>
        </div>
        <div className="mt-8 pt-8 border-t border-border" data-testid="resale-coefficient-panel">
          <p className="text-xs text-muted-foreground eyebrow mb-3 flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5" /> Coefficient de revente
          </p>
          <p className="text-xs text-muted-foreground mb-3 max-w-md">
            Appliquez votre propre majoration à vos futurs devis. Seul ce coefficient est appliqué à vos devis — il est indépendant de celui de l'atelier.
          </p>
          <div className="flex items-center gap-2 max-w-xs">
            <Input data-testid="resale-coefficient-input" type="number" min="1" max="10" step="0.01" value={resaleCoefficient} onChange={(e) => setResaleCoefficient(e.target.value)} />
            <Button data-testid="save-resale-coefficient-button" size="sm" className="h-9" onClick={handleSaveResaleCoefficient} disabled={savingResaleCoefficient}>
              {savingResaleCoefficient ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </div>
        </div>
      </div>
    </main>
  );
}
